<script lang="ts" setup>
import { ref, reactive, computed, watch, onUpdated, onBeforeUnmount } from 'vue'
import type { PropType } from 'vue'
import Masonry from 'masonry-layout'
import axios from 'axios'
import { useStore } from '@nanostores/vue'
import { useVModels } from '@vueuse/core'
import {
	getProductSharingUrl,
	productSharingData,
	checkExpressCheckoutEnable,
	productSharingPostPerPage,
	getPostListModeMapping,
} from '@/legacy/ts/productSharing'
import useProductSharing from '@/legacy/ts/useProductSharing'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import BsButtonOutline from '@/legacy/components/ButtonOutline.vue'
import BsButtonNormal from '@/legacy/components/ButtonNormal.vue'
import BsRadio from '@/legacy/components/Radio.vue'
import BsCheckbox from '@/legacy/components/Checkbox.vue'
import BsModalDrawer from '@/legacy/components/ModalDrawer.vue'
import BsShowMore from '@/legacy/components/ShowMore.vue'
import { ButtonNormalTheme } from '@/types/ButtonLegacy'
import {
	fetchSettingSupportedCurrencies,
	instanceProductSharing,
	instanceBuyandship,
} from '@/assets/ts/apiManagement'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BsProductSharingPostListItem from '@/legacy/components/ProductSharingPostListItem.vue'
import CommunityFilterDialogSheet from '@/components/CommunityFilterDialogSheet.vue'
import { useBreakpoints, capitalizeFirstLetter } from '@/lib/utils'
import injectStrict from '@/lib/inject'
import { fromPostId } from '@/store/productSharingStore'
import ProductSharingEmpty from '@/components/ProductSharingEmpty.vue'

export interface ISorting {
	label: string
	value: string
}

interface IPostListMeta {
	count: number
	query: {
		country: string
		start: string
		limit: string
		sort: string
		express_checkout: boolean
	}
	total_count: number
}

interface IFilterBackup {
	sortMode: string
	filterWarehouses: string[]
	filterCategories: string[]
}

export interface ICategory {
	name: string
	slug: string
}

export interface IWarehouseList {
	id: string
	name: string
	countryIconUrl: string
}
const props = defineProps({
	username: {
		type: String,
		default: '',
	},
	columnCount: {
		type: Number,
		default: 3,
	},
	postPerPage: {
		type: Number,
		default: 3,
	},
	isSearch: {
		type: Boolean,
		default: false,
	},
	isExplore: {
		type: Boolean,
		default: false,
	},
	isIndexLogin: {
		type: Boolean,
		default: false,
	},
	searchKeyword: {
		type: String,
		default: '',
	},
	hashtagKeyword: {
		type: String,
		default: '',
	},
	text: {
		type: Object,
		default: null,
	},
	isCurrentModeDefault: {
		type: Boolean,
		default: true,
	},
	isCurrentModeBoutique: {
		type: Boolean,
		default: false,
	},
	showEmpty: {
		type: Boolean,
		default: true,
	},
	sortingOptions: {
		type: Array as PropType<ISorting[]>,
	},
	defaultSorting: {
		type: String,
	},
	categories: {
		type: Array as PropType<ICategory[]>,
	},
	localCurrencyInfo: {
		type: Array,
	},
	isDemoApi: {
		type: Boolean,
		defalut: false,
	},
	isRecommend: {
		type: Boolean,
		default: false,
	},
	currentPostId: {
		type: String,
		default: '',
	},
	isTypeModal: {
		type: Boolean,
		default: true,
	},
	currentMode: {
		type: String,
		default: '',
	},
	shouldOpenFilter: {
		type: Boolean,
		default: false,
	},
	currentBannerTab: {
		type: String,
		default: '',
	},
	currentSecondaryTab: {
		type: String,
		default: '',
	},
	currentCategoryAlias: {
		type: String,
		default: '',
	},
	warehouseIdMap: {
		type: Object,
		default: () => ({}),
	},
	warehouseList: {
		type: Array as PropType<IWarehouseList[]>,
		default: () => [],
	},
	supportedCurrencies: {
		type: Object,
		default: null,
	},
	isLoadingPrerequisites: {
		// If product sharing index is still loading prerequisites, don't show post list
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits([
	'post-item-click',
	'post-item-like',
	'update-meta',
	'setIsRecommendShow',
	'update-filter-count',
	'update:shouldOpenFilter',
	'warehouseAlias',
	'categoryAlias',
	'update-sort-mode',
])
const { shouldOpenFilter } = useVModels(props, emit)

const addMessage: (message: string) => void = injectStrict('addMessage')
const postItemClickProvide: (({ postId }: { postId: string }) => void) | null =
	injectStrict('postItemClickProvide', null)
const setPostListInfoProvide: ((payload: any) => void) | null = injectStrict(
	'setPostListInfoProvide',
	null,
)

const fromPostIdGlobal = useStore(fromPostId)
const page = ref(1)
const postList = ref<[] | null>(null)
const pagedList = ref<[] | null>(null)
const isLoading = ref(true)
const isLoadingMorePost = ref(false)
const isShowFilter = ref(false)
const isResultFromFilter = ref(false)
const isExpressCheckoutEnable = ref(checkExpressCheckoutEnable())
const filterWarehouses = ref<string[]>([])
const sortMode = ref('recommended')
const services = ref([])
const filterServices = ref([])
const isFilterChangedWithoutApply = ref(true)
const filterBackup: IFilterBackup = reactive({
	sortMode: props.defaultSorting || 'recommended',
	filterWarehouses: [],
	filterCategories: [],
})
const meta = ref<IPostListMeta | null>(null)
const countryListDirection = ref('down')
const categoryListDirection = ref('down')
const filterCategories = ref<string[]>([])
const masonryInstance = ref<Masonry | null>(null)

const { isUserVerified, setToken } = useProductSharing()
const { isLargeOld: isDesktop } = useBreakpoints()
const isPostEmpty = computed(() => {
	return postList.value && !postList.value.length
})

const isResetDisabled = computed(() => {
	if (!props.isExplore) return true
	return (
		sortMode.value === props.defaultSorting &&
		!filterWarehouses.value.length &&
		!filterCategories.value.length
	)
})

const currentSortingOptions = computed(() => {
	if (!props.isExplore || !props.sortingOptions) return null
	const optionsBackup = [...(<ISorting[]>props.sortingOptions)]
	if (!props.isCurrentModeDefault) {
		optionsBackup.splice(0, 1)
		return optionsBackup
	}
	return optionsBackup
})

const isNextPageExist = computed(() => {
	if (meta.value) {
		return (
			meta.value.total_count >
			Number(meta.value.query.start) + Number(meta.value.query.limit) - 1
		)
	}
	return false
})

const filterCount = computed(() => {
	if (!props.isExplore) return 0
	if (Object.keys(filterBackup).length) {
		return (
			filterBackup.filterCategories.length +
			filterBackup.filterWarehouses.length +
			(filterBackup.sortMode === props.defaultSorting ? 0 : 1)
		)
	}
	return 0
})

const isCountryListExpand = computed(() => {
	return countryListDirection.value === 'up'
})

const isCategoryListExpand = computed(() => {
	return categoryListDirection.value === 'up'
})

const currentGaCategory = computed(() => {
	const dict = getPostListModeMapping('ga')

	return dict[props.currentMode] || ''
})

const gridId = computed(() => {
	return props.isRecommend ? `grid_${props.currentPostId}` : undefined
})

const currentFromPostId = computed(() => {
	const fromPostId = new URLSearchParams(window.location.search).get(
		'fromPostId',
	)
	if (!props.isTypeModal) {
		return props.currentPostId
	}

	if (fromPostId) {
		return fromPostId
	}

	if (fromPostIdGlobal.value && fromPostIdGlobal.value) {
		return fromPostIdGlobal.value
	}

	return ''
})

const currentLocation = computed(() => {
	if (props.isSearch) return 'search'
	if (props.isExplore) return 'explore'
	if (props.isIndexLogin) return 'home'
	return 'public_profile'
})

function postPageLoadGA() {
	if (!props.isExplore && !props.isSearch) {
		return
	}

	createGAEvent(GaGeneralEvent.PageLoad, {
		page_name: GaGeneralPageName.CommunityPostListingPage,
		tab_name: `${capitalizeFirstLetter(
			props.currentBannerTab,
		)}-${capitalizeFirstLetter(props.currentSecondaryTab)}`,
		sort_option: capitalizeFirstLetter(sortMode.value),
		warehouse_filter: `${convertWarehouseIdToAlias()}`,
		category_filter: `${filterCategories.value.join(',') || 'all'}`,
		keyword: `${props.searchKeyword}`,
	})
}
function postViewItemListGA() {
	const obj = {
		location_id: GaGeneralPageName.CommunityPostListingPage,
		item_list_name: 'Community Post Listing',
		tab_name: `${capitalizeFirstLetter(
			props.currentBannerTab,
		)}-${capitalizeFirstLetter(props.currentSecondaryTab)}`,
		sort_option: capitalizeFirstLetter(sortMode.value),
		warehouse_filter: `${convertWarehouseIdToAlias()}`,
		category_filter: `${filterCategories.value.join(',') || 'all'}`,
		keyword: `${props.searchKeyword}`,
	}

	const updatedPostList = postList.value?.map((post, index) => {
		const typedPost = post as {
			id: string
			itemName: string
			isExpressCheckout: boolean
		}

		return {
			...obj,
			item_id: `post_${typedPost.id}`,
			item_name: typedPost.itemName,
			item_category: typedPost.isExpressCheckout
				? 'Express Checkout'
				: 'Normal Request',
			index,
		}
	})
	createGAEvent('view_item_list', {
		items: updatedPostList,
	})
}
function updateSessionStorageWithFilters() {
	const filters = {
		sortMode: sortMode.value,
		filterWarehouses: filterWarehouses.value,
		filterCategories: filterCategories.value,
	}
	sessionStorage.setItem('productSharingFilters', JSON.stringify(filters))
}

function getFiltersFromSessionStorage() {
	const storedFilters = sessionStorage.getItem('productSharingFilters')
	if (!storedFilters) return

	const filters = JSON.parse(storedFilters)
	if (filters.sortMode) sortMode.value = filters.sortMode
	if (filters.filterWarehouses)
		filterWarehouses.value = filters.filterWarehouses
	if (filters.filterCategories)
		filterCategories.value = filters.filterCategories
}

function fetchPostList(isFromLoadMore = false) {
	const start = page.value > 1 ? (page.value - 1) * props.postPerPage + 1 : 1
	return new Promise((resolve, reject) => {
		const sortSuffix = `&sort=${sortMode.value}`
		const dict = getPostListModeMapping('api')
		// Set default mode to recommended to prevent 404 undefined error
		const modeSuffix = dict[props.currentMode] || dict.recommended

		const genericSuffix = `limit=${
			props.isIndexLogin ? 12 : props.postPerPage
		}&start=${start}`
		const interestSuffix =
			props.isExplore && filterCategories.value.length
				? `&interest=${filterCategories.value.toString()}`
				: ''
		const warehouseSuffix =
			props.isExplore && filterWarehouses.value.length
				? `&warehouse=${filterWarehouses.value.toString()}`
				: ''

		let apiPath = `/posts?${genericSuffix}${sortSuffix}`
		if (props.isExplore) {
			apiPath = `/posts/${modeSuffix}?${genericSuffix}${sortSuffix}${interestSuffix}${warehouseSuffix}`
		} else if (props.isIndexLogin) {
			apiPath = `/posts?${genericSuffix}${sortSuffix}`
		} else if (props.isSearch && props.hashtagKeyword) {
			const result = props.hashtagKeyword
				.split(',')
				.map(item => `#${item}`)
				.join(' ')
			apiPath = `/posts?${genericSuffix}&hashtag=${encodeURIComponent(result)}`
		} else if (props.isSearch && !props.hashtagKeyword) {
			apiPath = `/search?${genericSuffix}&q=${encodeURIComponent(
				props.searchKeyword,
			)}`
		} else if (props.username !== '') {
			apiPath = `/posts?${genericSuffix}&username=${props.username}`
		}

		if (props.isDemoApi) {
			apiPath = `/recommendations?limit=${props.postPerPage}&start=${start}`
		}

		if (props.isRecommend) {
			apiPath = `/recommendations?limit=${productSharingPostPerPage.recommend}&start=${start}&type=post_detail&current_post_id=${props.currentPostId}`
		}

		instanceProductSharing
			.get<{
				data: any
				meta: IPostListMeta
			}>(apiPath)
			.then(({ data: { data, meta: metaResult } }) => {
				if (!data) reject()
				if (props.isSearch) emit('update-meta', metaResult)
				meta.value = metaResult
				const resultList = data.map((post: any) => ({
					itemName: post.item_name,
					itemUrl: post.item_url,
					itemWeight: post.item_weight,
					originalPrice: post.original_price,
					itemUnitPrice: post.item_unit_price,
					paymentMethod: post.payment_method,
					site: post.site,
					id: post.id,
					orderId: post.order_id,
					slug: post.ec_product_slug,
					status: post.status,
					title: post.title,
					content: post.content,
					createdAt: post.created_at,
					updatedAt: post.updated_at,
					publishDate: post.publish_date,
					hashtags: post.hashtags,
					category: post.category,
					likeCount: post.like_count,
					bookmarkCount: post.bookmark_count,
					shareCount: post.share_count,
					reportCount: post.report_count,
					media: post.media,
					mediaRatio: post.media_ratio,
					user: post.user,
					isLiked: post.is_liked,
					isExpressCheckout:
						isExpressCheckoutEnable.value && post.can_express_checkout,
					warehouse: post.warehouse,
					ecLocalPrice: post.ec_local_price,
					ecPrice: post.ec_price,
				}))

				if (resultList.length === 0) {
					if (page.value === 1) {
						postList.value = []
						if (props.isRecommend) {
							setIsRecommendShow(false)
						}
					}
					if (postList.value?.length) addMessage(props.text.msg.emptyViewMore)
				} else {
					pagedList.value =
						start > 1 ? [...(<[]>pagedList.value), ...resultList] : resultList
					const uniqueIds: string[] = []
					postList.value = pagedList.value?.filter((p: { id: string }) => {
						const isDuplicated = uniqueIds.includes(p.id)
						if (!isDuplicated) {
							uniqueIds.push(p.id)
							return true
						}
						return false
					}) as any
				}
			})
			.then(() => {
				isLoadingMorePost.value = false
				resolve(true)
				postPageLoadGA()
				postViewItemListGA()
			})
			.catch(error => {
				if (props.isRecommend) {
					setIsRecommendShow(false)
				}
				reject(error)
			})
	})
}

function loadMore() {
	if (!isNextPageExist.value) return
	isLoadingMorePost.value = true
	page.value += 1
	fetchPostList(true)
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.CommunityPostListingPage,
		button_name: 'View More',
		tab_name: `${capitalizeFirstLetter(
			props.currentBannerTab,
		)}-${capitalizeFirstLetter(props.currentSecondaryTab)}`,
		sort_option: capitalizeFirstLetter(sortMode.value),
		warehouse_filter: `${convertWarehouseIdToAlias()}`,
		category_filter: `${filterCategories.value.join(',') || 'all'}`,
		keyword: `${props.searchKeyword}`,
	})
}

function initList() {
	const target = gridId.value ? `#${gridId.value}` : '.mansory-grid'
	const grid = document.querySelector(target)
	if (grid) {
		masonryInstance.value = new Masonry(grid, {
			itemSelector: '.grid-item',
		})
	}
}

function sendEvent(name: string) {
	if (props.isExplore || props.isIndexLogin)
		createGAEvent(name, { category: 'product_sharing' })
}

function postItemClick(
	post: {
		id: string
		slug: string
		itemName: string
		isExpressCheckout: boolean
	},
	index: number,
) {
	const itemCategory = post.isExpressCheckout
		? 'Express Checkout'
		: 'Normal Request'

	createGAEvent('select_item', {
		items: [
			{
				location_id: GaGeneralPageName.CommunityPostListingPage,
				item_list_name: 'Community Post Listing',
				item_id: `post_${post.id}`,
				item_name: `${post.itemName}`,
				item_category: `${itemCategory}`,
				index,
				tab_name: `${capitalizeFirstLetter(
					props.currentBannerTab,
				)}-${capitalizeFirstLetter(props.currentSecondaryTab)}`,
				sort_option: capitalizeFirstLetter(sortMode.value),
				warehouse_filter: `${convertWarehouseIdToAlias()}`,
				category_filter: `${filterCategories.value.join(',') || 'all'}`,
				keyword: `${props.searchKeyword}`,
			},
		],
	})
	createGAEvent('ps_post_clicked', {
		category: post.slug || 'none',
		product_slug: post.slug || 'none',
		label: post.id,
		post_id: post.id,
		value: currentLocation.value,
		from: currentLocation.value,
		from_post_id: currentFromPostId.value,
		sequence_id: index,
	})
	sendEvent('c_item_f_ps_waterfall')
	if (postItemClickProvide) {
		postItemClickProvide({
			postId: post.id,
		})
	} else {
		window.location.href = getProductSharingUrl('post', { id: post.id })
	}
}

function setPostListAction(payload: {
	id: string
	param: string
	val: any
	likeCount: number
}) {
	if (payload && payload.id) {
		const currentPostIndex = postList.value?.findIndex(
			(obj: { id: string }) => obj.id === payload.id,
		)
		if (currentPostIndex === -1 || currentPostIndex === undefined) return
		;(postList.value as any)[currentPostIndex][payload.param] = payload.val
		if (payload.likeCount !== undefined)
			(postList.value as any)[currentPostIndex].likeCount = payload.likeCount
	}
}

function emitPostLike(payload: { id: string; slug: string }, index: number) {
	if (setPostListInfoProvide) {
		setPostListInfoProvide(payload)
	}
	emit('post-item-like', payload)
	createGAEvent('ps_post_liked', {
		// duplicated column should be removed in the future
		category: payload.slug || 'none',
		product_slug: payload.slug || 'none',
		label: payload.id,
		post_id: payload.id,
		value: currentLocation.value,
		from: currentLocation.value,
		from_post_id: currentFromPostId.value,
		sequence_id: index,
	})
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.CommunityPostListingPage,
		tab_name: `${capitalizeFirstLetter(
			props.currentBannerTab,
		)}-${capitalizeFirstLetter(props.currentSecondaryTab)}`,
		sort_option: capitalizeFirstLetter(sortMode.value),
		warehouse_filter: `${convertWarehouseIdToAlias()}`,
		category_filter: `${filterCategories.value.join(',') || 'all'}`,
		keyword: `${props.searchKeyword}`,
		post_id: payload.id,
		product_id: `${payload.slug}`,
		button_name: 'Community Post Like',
		button_object_id: `post_${payload.id}`,
	})
}

function showFilter() {
	if (isLoading.value) return
	isShowFilter.value = true
}

function hideFilter() {
	isShowFilter.value = false
}

function sendApplyGA(type: string) {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.CommunityPostListingPage,
		button_name: `${type}`,
		tab_name: `${capitalizeFirstLetter(
			props.currentBannerTab,
		)}-${capitalizeFirstLetter(props.currentSecondaryTab)}`,
		sort_option: capitalizeFirstLetter(sortMode.value),
		warehouse_filter: `${convertWarehouseIdToAlias()}`,
		category_filter: `${filterCategories.value.join(',') || 'all'}`,
		keyword: `${props.searchKeyword}`,
	})
}

async function fetchFilterList() {
	isFilterChangedWithoutApply.value = false
	sendSortGaEvent()
	sendWarehouseGaEvent()
	if (filterCategories.value.length && sortMode.value === 'recommended') {
		sendApplyGA('Filter Applied')
	} else if (
		filterWarehouses.value.length &&
		sortMode.value === 'recommended'
	) {
		sendApplyGA('Filter Applied')
	} else if (
		!filterCategories.value.length &&
		!filterWarehouses.value.length &&
		sortMode.value !== 'recommended'
	) {
		sendApplyGA('Sorting Applied')
	} else if (
		filterCategories.value.length &&
		filterWarehouses.value.length &&
		sortMode.value !== 'recommended'
	) {
		sendApplyGA('Filter & Sorting Applied')
	}
	hideFilter()
	isLoading.value = true
	updateSessionStorageWithFilters()
	await fetchPostList()
	isResultFromFilter.value = true
	isLoading.value = false
}

function resetSorting() {
	if (props.defaultSorting) {
		sortMode.value = props.defaultSorting
	}
}

async function resetFilter(isApply = false) {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.CommunityPostListingPage,
		button_name: 'Reset Filter',
		tab_name: `${capitalizeFirstLetter(
			props.currentBannerTab,
		)}-${capitalizeFirstLetter(props.currentSecondaryTab)}`,
		sort_option: capitalizeFirstLetter(sortMode.value),
		warehouse_filter: `${convertWarehouseIdToAlias()}`,
		category_filter: `${filterCategories.value.join(',') || 'all'}`,
		keyword: `${props.searchKeyword}`,
	})
	resetSorting()
	filterWarehouses.value = []
	filterCategories.value = []
	if (isApply) {
		isLoading.value = true
		isResultFromFilter.value = false
		meta.value = null
		setFilterBackup()
		sendSortGaEvent()
		sendWarehouseGaEvent()
		updateSessionStorageWithFilters()
		await fetchPostList()
		isLoading.value = false
	}
}

function setFilterBackup() {
	filterBackup.sortMode = sortMode.value
	filterBackup.filterWarehouses = [...filterWarehouses.value]
	filterBackup.filterCategories = [...filterCategories.value]
	resetShowMore()
	isFilterChangedWithoutApply.value = true
}

function recoverFilter() {
	sortMode.value = filterBackup.sortMode
	filterWarehouses.value = filterBackup.filterWarehouses
	filterCategories.value = filterBackup.filterCategories
}

function convertWarehouseIdToAlias() {
	const res: string[] = []
	filterWarehouses.value.forEach(item => {
		res.push(
			(props.warehouseIdMap[item as keyof typeof props.warehouseIdMap] as any)
				.alias,
		)
	})
	emit('warehouseAlias', res.join('-') || 'all')
	return res.join('-') || 'all'
}

function convertCategorySlugToAlias() {
	const res: string[] = []
	filterCategories.value.forEach(item => {
		res.push((item as any).alias)
	})
	emit('categoryAlias', filterCategories.value.join(',') || 'all')
	return res.join('-') || 'all'
}

function checkWarehouseSame() {
	const mapIdOriginal: any = {}
	if (!filterBackup.filterWarehouses.length && !filterWarehouses.value.length)
		return true
	if (filterBackup.filterWarehouses.length !== filterWarehouses.value.length)
		return false

	filterBackup.filterWarehouses.forEach(item => {
		mapIdOriginal[item] = 1
	})

	filterWarehouses.value.forEach(item => {
		if (mapIdOriginal[item]) {
			mapIdOriginal[item] += 1
		} else {
			return false
		}
	})

	return Object.values(mapIdOriginal).every((item: any) => item > 1)
}

function checkCategorySame() {
	const mapIdOriginal: any = {}
	if (!filterBackup.filterCategories.length && !filterCategories.value.length)
		return true
	if (filterBackup.filterCategories.length !== filterCategories.value.length)
		return false

	filterBackup.filterCategories.forEach(item => {
		mapIdOriginal[item] = 1
	})

	filterCategories.value.forEach(item => {
		if (mapIdOriginal[item]) {
			mapIdOriginal[item] += 1
		} else {
			return false
		}
	})

	return Object.values(mapIdOriginal).every((item: any) => item > 1)
}

function sendSortGaEvent() {
	if (sortMode.value !== filterBackup.sortMode) {
		resetPagination()
		createGAEvent('ps_sorting_applied', {
			category: currentGaCategory.value,
			label: sortMode.value,
		})
	}
}

function sendWarehouseGaEvent() {
	const isWarehouseSame = checkWarehouseSame()
	const isCategorySame = checkCategorySame()
	if (!(isWarehouseSame && isCategorySame)) {
		resetPagination()
		createGAEvent('ps_filter_applied', {
			category: currentGaCategory.value,
			label: convertWarehouseIdToAlias(),
			value: convertCategorySlugToAlias(),
		})
	}
}

function resetPagination() {
	page.value = 1
}

function toggleCountryListExpand() {
	countryListDirection.value = isCountryListExpand.value ? 'down' : 'up'
}

function resetCountryList() {
	countryListDirection.value = 'down'
}

function toggleCategoryListExpand() {
	categoryListDirection.value = isCategoryListExpand.value ? 'down' : 'up'
}

function resetCategoryList() {
	categoryListDirection.value = 'down'
}

function resetShowMore() {
	resetCountryList()
	resetCategoryList()
}

function setIsRecommendShow(isShow: boolean) {
	emit('setIsRecommendShow', isShow)
}

watch(
	() => filterCount.value,
	newCount => {
		emit('update-filter-count', newCount)
	},
	{ immediate: true },
)
;(async () => {
	if (
		productSharingData.isLogin &&
		(props.isExplore || props.isIndexLogin || props.isSearch) &&
		isUserVerified.value
	) {
		await setToken()
	}
	if (props.isExplore && props.sortingOptions !== undefined) {
		resetSorting()
		getFiltersFromSessionStorage()
		setFilterBackup()
	}

	await fetchPostList()

	isLoading.value = false
})()

watch(
	() => isShowFilter.value,
	value => {
		if (value) {
			setFilterBackup()
		}
		if (!value) {
			if (isFilterChangedWithoutApply.value) {
				setTimeout(() => {
					recoverFilter()
				}, 500)
			} else {
				setFilterBackup()
			}
		}
	},
)

watch(shouldOpenFilter, value => {
	if (value) {
		showFilter()
		shouldOpenFilter.value = false
	}
})

watch(
	() => sortMode.value,
	value => {
		emit('update-sort-mode', value)
	},
)

onUpdated(() => {
	if (postList.value) initList()
})

onBeforeUnmount(() => {
	if (masonryInstance.value) {
		masonryInstance.value.destroy?.()
	}
})

defineExpose({
	setPostListAction,
})
</script>

<template>
	<div
		:class="[
			'bs-product-sharing-post-list',
			{
				recommend: isRecommend,
				'new-gutter-of-search-and-index': isSearch || isExplore,
			},
		]"
	>
		<div class="bs-product-sharing-post-list__content">
			<!-- <div
				class="bs-product-sharing-post-list__filter-entrance"
				v-if="isExplore"
			>
				<div
					class="bs-product-sharing-post-list__filter-entrance-content"
					:class="{ active: filterCount }"
					@click="showFilter"
				>
					<i
						class="bs-product-sharing-post-list__filter-entrance-content-icon"
					></i>
					<div
						class="bs-product-sharing-post-list__filter-entrance-content-text"
					>
						{{ text.filter.entrance }} ({{ filterCount }})
					</div>
				</div>
			</div> -->
			<div class="mansory-grid" :id="gridId">
				<template
					v-if="isLoading || postList === null || isLoadingPrerequisites"
				>
					<BsProductSharingPostListItem
						v-for="(post, index) in postPerPage"
						:key="index"
						:isLoading="true"
						:class="[
							`bs-product-sharing-post-list__content-listing-col${columnCount}`,
						]"
					></BsProductSharingPostListItem>
				</template>
				<template v-else>
					<BsProductSharingPostListItem
						v-for="(post, index) in postList"
						:key="index"
						:post="post"
						:columnCount="columnCount"
						:class="[
							`bs-product-sharing-post-list__content-listing-col${columnCount}`,
						]"
						:text="text"
						:isSearch="isSearch"
						:isExplore="isExplore"
						:isIndexLogin="isIndexLogin"
						:localCurrencyInfo="localCurrencyInfo"
						:isCurrentModeDefault="isCurrentModeDefault"
						:supportedCurrencies="supportedCurrencies"
						:warehouseIdMap="warehouseIdMap"
						:gridId="gridId"
						@view-post="postItemClick(post, index)"
						@like="emitPostLike($event, index)"
					></BsProductSharingPostListItem>
					<template v-if="isLoadingMorePost">
						<BsProductSharingPostListItem
							v-for="length in postPerPage"
							:key="`loading${length}`"
							:isLoading="true"
							:class="[
								`bs-product-sharing-post-list__content-listing-col${columnCount}`,
							]"
						></BsProductSharingPostListItem>
					</template>
				</template>
			</div>
		</div>
		<div class="clear"></div>
		<div
			v-if="!isLoading && isNextPageExist && !isIndexLogin && postList?.length"
			class="bs-product-sharing-post-list__view-more-wrapper"
		>
			<BaseButton size="medium" variant="primary-inverse" @click="loadMore">
				{{ text.button.more }}
			</BaseButton>
		</div>
		<div
			v-if="isPostEmpty && !isLoading && showEmpty && !isSearch"
			class="bs-product-sharing-post-list__empty"
		>
			<ProductSharingEmpty
				v-if="filterCount === 0"
				:title="text.msg.defaultEmpty || text.msg.empty"
				:description="text.msg.defaultEmptyDescription || ''"
			></ProductSharingEmpty>
			<ProductSharingEmpty
				v-if="filterCount > 0"
				:title="text.msg.filterEmpty"
				:description="text.msg.filterEmptyDescription"
			>
				<template #button>
					<BaseButton
						size="medium"
						variant="primary-inverse"
						@click="resetFilter(true)"
					>
						{{ text.filter.button.emptyReset }}
					</BaseButton>
				</template>
			</ProductSharingEmpty>
		</div>
		<div v-if="isPostEmpty && !isLoading && showEmpty && isSearch">
			<ProductSharingEmpty
				:isSearch="true"
				:description="text.msg.searchEmptyDescription"
			/>
		</div>

		<CommunityFilterDialogSheet
			v-if="isExplore"
			v-model:isOpen="isShowFilter"
			v-model:sortMode="sortMode"
			v-model:filterWarehouses="filterWarehouses"
			v-model:filterCategories="filterCategories"
			:sortOptions="sortingOptions"
			:warehouseOptions="warehouseList"
			:categoryOptions="categories"
			:filterSortTitles="text.filter.title"
			:filterSortButtons="text.filter.button"
			:isResetDisabled="isResetDisabled"
			@clearAll="resetFilter(false)"
			@apply="fetchFilterList"
		/>

		<!-- <Teleport to="body">
			<BsModalDrawer
				v-if="isExplore"
				v-model="isShowFilter"
				class="bs-product-sharing-post-list__modal-drawer"
				:title="text.filter.title.drawer"
			>
				<div class="bs-product-sharing-post-list__filter">
					<div class="bs-product-sharing-post-list__filter-title sort">
						{{ text.filter.title.sort }}
					</div>
					<div
						class="bs-product-sharing-post-list__filter-content three-column"
					>
						<BsRadio
							v-for="item in currentSortingOptions"
							v-model="sortMode"
							:name="item.value"
							:isSpaceBetween="true"
							:key="item.value"
						>
							<template #content>
								<div class="bs-product-sharing-post-list__filter-label-text">
									{{ item.label }}
								</div>
							</template>
						</BsRadio>
					</div>
					<div class="bs-product-sharing-post-list__filter-title">
						{{ text.filter.title.warehouse }}
					</div>
					<div
						class="bs-product-sharing-post-list__filter-content"
						:class="{ isExpand: isCountryListExpand }"
					>
						<BsCheckbox
							v-for="item in warehouseList"
							v-model="filterWarehouses"
							:id="item.id"
							:isSpaceBetween="true"
							:key="item.id"
						>
							<template #content>
								<div class="bs-product-sharing-post-list__filter-label">
									<img
										class="bs-product-sharing-post-list__filter-label-image"
										:src="item.countryIconUrl"
									/>
									<span class="bs-product-sharing-post-list__filter-label-text">
										{{ item.name }}
									</span>
								</div>
							</template>
						</BsCheckbox>
					</div>
					<BsShowMore
						v-if="!isDesktop"
						:text="
							isCountryListExpand
								? text.filter.button.showLess
								: text.filter.button.showMore
						"
						:direction="countryListDirection"
						@click.native="toggleCountryListExpand"
					/>
					<div class="bs-product-sharing-post-list__filter-title far">
						{{ text.filter.title.categories }}
					</div>
					<div
						class="bs-product-sharing-post-list__filter-content category"
						:class="{ isExpand: isCategoryListExpand }"
					>
						<BsCheckbox
							class="bs-product-sharing-post-list__filter__category"
							v-for="item in categories"
							v-model="filterCategories"
							:id="item.slug"
							:isSpaceBetween="true"
							:key="item.slug"
						>
							<template #content>
								<div class="bs-product-sharing-post-list__filter-label-text">
									{{ item.name }}
								</div>
							</template>
						</BsCheckbox>
					</div>
					<BsShowMore
						v-if="!isDesktop"
						:text="
							isCategoryListExpand
								? text.filter.button.showLess
								: text.filter.button.showMore
						"
						:direction="categoryListDirection"
						@click.native="toggleCategoryListExpand"
					/>
				</div>
				<div class="bs-product-sharing-post-list__filter__sticky-bottom">
					<div
						class="bs-product-sharing-post-list__filter-button-reset"
						:class="{
							'bs-product-sharing-post-list__filter-button-reset--disabled':
								isResetDisabled,
						}"
						@click="resetFilter(false)"
					>
						{{ text.filter.button.reset }}
					</div>
					<BsButtonNormal
						class="bs-product-sharing-post-list__filter-button-apply"
						:action="fetchFilterList"
						:text="text.filter.button.apply"
						:theme="
							isDesktop
								? ButtonNormalTheme.NaturalLarge
								: ButtonNormalTheme.Normal
						"
					></BsButtonNormal>
				</div>
			</BsModalDrawer>
		</Teleport> -->
	</div>
</template>
